import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { 
  collection, 
  query, 
  orderBy, 
  onSnapshot, 
  addDoc, 
  deleteDoc, 
  doc, 
  updateDoc,
  serverTimestamp,
  Timestamp,
  getDoc,
  limit,
  runTransaction
} from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import Header from '../components/Header';
import Toast from '../components/Toast';
import MapComponent from '../components/MapComponent';
import AvailabilityManager from '../components/admin/AvailabilityManager';
import loggerInstance from '../utils/logger';
import { formatDisplayDateTime, formatDisplayDate } from '../utils/dateUtils';
import { escapeAndTruncate } from '../utils/textUtils';

const AdminDashboard = () => {
  const [user, setUser] = useState(null);
  const [toast, setToast] = useState(null);
  const [activeTab, setActiveTab] = useState('submissions');
  const [logs, setLogs] = useState([]);
  const [isCachedData, setIsCachedData] = useState(false);

  // Subscribe to logs
  useEffect(() => {
    setLogs(loggerInstance.getLogs());
    const unsubscribe = loggerInstance.subscribe((newLogs) => {
      setLogs(newLogs);
    });
    return () => unsubscribe();
  }, []);
  
  // Collections
  const [helpRequests, setHelpRequests] = useState([]);
  const [campReports, setCampReports] = useState([]);
  const [helpOffers, setHelpOffers] = useState([]);
  const [events, setEvents] = useState([]);
  const [needs, setNeeds] = useState([]);
  const [resources, setResources] = useState([]);

  // Queue Triage Filters
  const [triageQueue, setTriageQueue] = useState('all');
  const [hideResolved, setHideResolved] = useState(true);
  const [failedOnly, setFailedOnly] = useState(false);

  // Detail / Triage Modal States
  const [activeRequestDetail, setActiveRequestDetail] = useState(null);
  const [activeCampDetail, setActiveCampDetail] = useState(null);
  const [activeOfferDetail, setActiveOfferDetail] = useState(null);

  // Resource Form States
  const [showResourceForm, setShowResourceForm] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [resourceForm, setResourceForm] = useState({
    name: '', categories: '', phone: '', website: '', serviceArea: '', hours: '', eligibility: '', publicDescription: '', notes: '', active: true
  });

  // Event Form States
  const [showEventForm, setShowEventForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventForm, setEventForm] = useState({ title: '', description: '', date: '' });

  // Need Form States
  const [showNeedForm, setShowNeedForm] = useState(false);
  const [editingNeed, setEditingNeed] = useState(null);
  const [needForm, setNeedForm] = useState({ title: '', description: '' });

  // Availability Settings States
  const [availability, setAvailability] = useState({
    status: 'normal',
    message: '',
    expectedReviewDate: '',
    expirationDate: '',
    acceptHelpRequests: true,
    acceptCampReports: true,
    acceptVolunteerOffers: true
  });

  const [healthStatus, setHealthStatus] = useState({
    network: 'pending',
    firestore: 'pending',
    localStorage: 'pending',
    indexedDb: 'pending',
    geolocation: 'pending'
  });
  const [runningDiagnostics, setRunningDiagnostics] = useState(false);

  const runHealthChecks = async () => {
    setRunningDiagnostics(true);
    const status = {
      network: navigator.onLine ? 'online' : 'offline',
      firestore: 'pending',
      localStorage: 'pending',
      indexedDb: 'pending',
      geolocation: 'pending'
    };

    // Test LocalStorage
    try {
      localStorage.setItem('health_test', 'ok');
      localStorage.removeItem('health_test');
      status.localStorage = 'functional';
    } catch (e) {
      status.localStorage = 'failed';
    }

    // Test IndexedDB
    try {
      if (window.indexedDB) {
        status.indexedDb = 'functional';
      } else {
        status.indexedDb = 'unsupported';
      }
    } catch (e) {
      status.indexedDb = 'failed';
    }

    // Test Geolocation Permission
    try {
      if (navigator.permissions && navigator.permissions.query) {
        const result = await navigator.permissions.query({ name: 'geolocation' });
        status.geolocation = result.state;
      } else {
        status.geolocation = 'unsupported';
      }
    } catch (e) {
      status.geolocation = 'unknown';
    }

    // Test Firestore
    try {
      const docRef = doc(db, 'availability', 'settings');
      await getDoc(docRef);
      status.firestore = 'connected';
    } catch (err) {
      console.error('Firestore health check failed:', err);
      status.firestore = 'disconnected';
    }

    setHealthStatus(status);
    setRunningDiagnostics(false);
  };

  const navigate = useNavigate();

  // Authentication & Authorization check
  useEffect(() => {
    if (user) {
      runHealthChecks();
    }
  }, [user]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists() && (userDoc.data().role === 'admin' || userDoc.data().role === 'staff')) {
            setUser(currentUser);
          } else {
            await signOut(auth);
            navigate('/admin/login');
          }
        } catch (err) {
          console.error("Auth check failed:", err);
          navigate('/admin/login');
        }
      } else {
        navigate('/admin/login');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Firestore SNAPSHOTS
  useEffect(() => {
    if (!user) return;

    // Bound sensitive submission loads — newest first (privacy + browser memory)
    const SUBMISSION_PAGE_SIZE = 100;

    const unsubscribeHelpRequests = onSnapshot(
      query(collection(db, 'help_requests'), orderBy('timestamp', 'desc'), limit(SUBMISSION_PAGE_SIZE)),
      (snapshot) => {
        setHelpRequests(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
        if (snapshot.metadata) {
          setIsCachedData(snapshot.metadata.fromCache);
        }
      },
      (error) => {
        console.error('Error loading help requests:', error?.code || error);
        setToast({
          message:
            error?.code === 'failed-precondition'
              ? 'Help requests query needs a Firestore index. Check the Firebase console link in the browser error log.'
              : 'Could not load help requests.',
          type: 'error',
        });
      }
    );

    const unsubscribeCampReports = onSnapshot(
      query(collection(db, 'camp_reports'), orderBy('timestamp', 'desc'), limit(SUBMISSION_PAGE_SIZE)),
      (snapshot) => {
        setCampReports(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
        if (snapshot.metadata) {
          setIsCachedData(snapshot.metadata.fromCache);
        }
      },
      (error) => {
        console.error('Error loading camp reports:', error);
        setToast({ message: 'Could not load camp reports.', type: 'error' });
      }
    );

    const unsubscribeHelpOffers = onSnapshot(
      query(collection(db, 'help_offers'), orderBy('timestamp', 'desc'), limit(SUBMISSION_PAGE_SIZE)),
      (snapshot) => {
        setHelpOffers(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
        if (snapshot.metadata) {
          setIsCachedData(snapshot.metadata.fromCache);
        }
      },
      (error) => {
        console.error('Error loading help offers:', error);
        setToast({ message: 'Could not load volunteer offers.', type: 'error' });
      }
    );

    const unsubscribeEvents = onSnapshot(
      query(collection(db, 'events'), orderBy('createdAt', 'desc'), limit(50)),
      (snapshot) => {
        setEvents(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (error) => {
        console.error('Error loading events:', error);
      }
    );

    const unsubscribeNeeds = onSnapshot(
      query(collection(db, 'needs'), orderBy('createdAt', 'desc'), limit(50)),
      (snapshot) => {
        setNeeds(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (error) => {
        console.error('Error loading needs:', error);
      }
    );

    const unsubscribeResources = onSnapshot(
      query(collection(db, 'resources'), orderBy('name', 'asc'), limit(100)),
      (snapshot) => {
        setResources(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (error) => {
        console.error('Error loading resources:', error);
      }
    );

    // Get current availability status settings
    getDoc(doc(db, 'availability', 'settings')).then((snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setAvailability({
          status: data.status || 'normal',
          message: data.message || '',
          expectedReviewDate: data.expectedReviewDate ? data.expectedReviewDate.toDate().toISOString().substring(0, 16) : '',
          expirationDate: data.expirationDate ? data.expirationDate.toDate().toISOString().substring(0, 16) : '',
          acceptHelpRequests: data.acceptHelpRequests !== false,
          acceptCampReports: data.acceptCampReports !== false,
          acceptVolunteerOffers: data.acceptVolunteerOffers !== false
        });
      }
    });

    return () => {
      unsubscribeHelpRequests();
      unsubscribeCampReports();
      unsubscribeHelpOffers();
      unsubscribeEvents();
      unsubscribeNeeds();
      unsubscribeResources();
    };
  }, [user]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const copyText = async (text, label = 'Copied') => {
    if (!text) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(String(text));
      } else {
        // Fallback for older browsers / insecure contexts
        const ta = document.createElement('textarea');
        ta.value = String(text);
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setToast({ message: `${label} to clipboard.`, type: 'success' });
    } catch (error) {
      setToast({ message: 'Could not copy.', type: 'error' });
    }
  };



  const isSafeHttpUrl = (value) => {
    if (!value) return true;
    try {
      const u = new URL(value);
      return u.protocol === 'https:' || u.protocol === 'http:';
    } catch {
      return false;
    }
  };

  const normalizePhoneNumber = (phone) => {
    if (!phone) return null;
    const cleaned = ('' + phone).replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `${match[1]}-${match[2]}-${match[3]}`;
    }
    return phone;
  };

  // CRUD Resources
  const handleResourceSubmit = async (e) => {
    e.preventDefault();
    const website = resourceForm.website.trim();
    if (website && !isSafeHttpUrl(website)) {
      setToast({ message: 'Website must be a full http:// or https:// URL.', type: 'error' });
      return;
    }
    try {
      const resourceData = {
        name: resourceForm.name.trim(),
        categories: resourceForm.categories.trim().split(',').map(s => s.trim()).filter(Boolean),
        phone: normalizePhoneNumber(resourceForm.phone.trim()) || null,
        website: website || null,
        serviceArea: resourceForm.serviceArea.trim() || null,
        hours: resourceForm.hours.trim() || null,
        eligibility: resourceForm.eligibility.trim() || null,
        publicDescription: resourceForm.publicDescription.trim() || null,
        notes: resourceForm.notes.trim() || null,
        active: !!resourceForm.active,
        lastVerified: serverTimestamp()
      };

      if (editingResource) {
        await updateDoc(doc(db, 'resources', editingResource.id), resourceData);
        setToast({ message: 'Resource updated.', type: 'success' });
      } else {
        await addDoc(collection(db, 'resources'), resourceData);
        setToast({ message: 'Resource added.', type: 'success' });
      }

      setResourceForm({ name: '', categories: '', phone: '', website: '', serviceArea: '', hours: '', eligibility: '', publicDescription: '', notes: '', active: true });
      setShowResourceForm(false);
      setEditingResource(null);
    } catch (error) {
      setToast({ message: 'Error saving resource.', type: 'error' });
    }
  };

  const handleDeleteResource = async (id) => {
    if (!window.confirm('Delete this resource referral?')) return;
    try {
      await deleteDoc(doc(db, 'resources', id));
      setToast({ message: 'Resource deleted.', type: 'success' });
    } catch (error) {
      setToast({ message: 'Failed to delete.', type: 'error' });
    }
  };

  // CRUD Events
  const handleEventSubmit = async (e) => {
    e.preventDefault();
    try {
      const eventData = {
        title: eventForm.title.trim(),
        description: eventForm.description.trim(),
        date: eventForm.date ? Timestamp.fromDate(new Date(eventForm.date + 'T00:00:00')) : null,
        updatedAt: serverTimestamp()
      };

      if (editingEvent) {
        await updateDoc(doc(db, 'events', editingEvent.id), eventData);
        setToast({ message: 'Event updated.', type: 'success' });
      } else {
        eventData.createdAt = serverTimestamp();
        await addDoc(collection(db, 'events'), eventData);
        setToast({ message: 'Event added.', type: 'success' });
      }

      setEventForm({ title: '', description: '', date: '' });
      setShowEventForm(false);
      setEditingEvent(null);
    } catch (error) {
      setToast({ message: 'Error saving event.', type: 'error' });
    }
  };

  const handleDeleteEvent = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      await deleteDoc(doc(db, 'events', id));
      setToast({ message: 'Event deleted.', type: 'success' });
    } catch (error) {
      setToast({ message: 'Failed to delete.', type: 'error' });
    }
  };

  // CRUD Needs
  const handleNeedSubmit = async (e) => {
    e.preventDefault();
    try {
      const needData = {
        title: needForm.title.trim(),
        description: needForm.description.trim(),
        updatedAt: serverTimestamp()
      };

      if (editingNeed) {
        await updateDoc(doc(db, 'needs', editingNeed.id), needData);
        setToast({ message: 'Need updated.', type: 'success' });
      } else {
        needData.createdAt = serverTimestamp();
        await addDoc(collection(db, 'needs'), needData);
        setToast({ message: 'Need added.', type: 'success' });
      }

      setNeedForm({ title: '', description: '' });
      setShowNeedForm(false);
      setEditingNeed(null);
    } catch (error) {
      setToast({ message: 'Error saving need.', type: 'error' });
    }
  };

  const handleDeleteNeed = async (id) => {
    if (!window.confirm('Delete this need?')) return;
    try {
      await deleteDoc(doc(db, 'needs', id));
      setToast({ message: 'Need deleted.', type: 'success' });
    } catch (error) {
      setToast({ message: 'Failed to delete.', type: 'error' });
    }
  };

  // Status transitions only — closedAt/deleteAfter/statsCounted are owned by Cloud Functions
  const updateStatus = async (col, id, newStatus) => {
    try {
      const updateData = { status: newStatus, updatedAt: serverTimestamp() };

      await updateDoc(doc(db, col, id), updateData);
      setToast({ message: `Status updated to ${newStatus}.`, type: 'success' });
      
      // Update local modal view references
      if (activeRequestDetail && activeRequestDetail.id === id) {
        setActiveRequestDetail({ ...activeRequestDetail, ...updateData });
      }
      if (activeOfferDetail && activeOfferDetail.id === id) {
        setActiveOfferDetail({ ...activeOfferDetail, ...updateData });
      }
    } catch (error) {
      setToast({ message: 'Failed to update status.', type: 'error' });
    }
  };

  // Save admin notes, logs, assignments
  const handleSaveRequestEdits = async (id, data) => {
    try {
      await updateDoc(doc(db, 'help_requests', id), {
        ...data,
        updatedAt: serverTimestamp()
      });
      setToast({ message: 'Request details updated successfully!', type: 'success' });
      setActiveRequestDetail(null);
    } catch (err) {
      setToast({ message: 'Error saving request details.', type: 'error' });
    }
  };

  const handleDeleteSubmission = async (col, id, label) => {
    if (!window.confirm(`Are you sure you want to delete this ${label} immediately? This cannot be undone.`)) return;
    try {
      await deleteDoc(doc(db, col, id));
      setToast({ message: `${label} deleted.`, type: 'success' });
      setActiveRequestDetail(null);
      setActiveCampDetail(null);
      setActiveOfferDetail(null);
    } catch (error) {
      setToast({ message: `Error deleting.`, type: 'error' });
    }
  };

  const hasFailedNotification = (docRow) =>
    String(docRow.notificationStatus || '').startsWith('failed');

  const getFilteredOffers = () => {
    return helpOffers.filter((o) => {
      if (failedOnly && !hasFailedNotification(o)) return false;
      const status = o.status || 'new';
      if (hideResolved) {
        return status !== 'completed' && status !== 'declined';
      }
      return true;
    });
  };

  // Filter queues logic
  const getFilteredRequests = () => {
    return helpRequests.filter(r => {
      if (failedOnly && !hasFailedNotification(r)) return false;
      const matchesResolved = hideResolved ? (r.status || 'new') !== 'resolved' && (r.status || 'new') !== 'closed' && (r.status || 'new') !== 'unable_to_assist' && (r.status || 'new') !== 'spam' && (r.status || 'new') !== 'duplicate' : true;
      if (!matchesResolved) return false;

      switch(triageQueue) {
        case 'new':
          return (r.status === 'new');
        case 'needs_review':
          return (r.status === 'needs_review');
        case 'urgent':
          return (r.urgency === 'as_soon_as_available' || r.safetyConcern === true);
        case 'contact_requested':
          return (r.preferredContact !== 'none');
        case 'waiting_supplies':
          return (r.status === 'waiting_supplies');
        case 'referral_needed':
          return (r.requestedCategories && r.requestedCategories.includes('referral')) || r.status === 'referral_needed';
        case 'outreach_planned':
          return (r.status === 'outreach_planned');
        case 'completed':
          return (r.status === 'resolved');
        case 'closed':
          return (r.status === 'closed');
        case 'unable_to_assist':
          return (r.status === 'unable_to_assist');
        case 'spam':
          return (r.status === 'spam');
        case 'duplicate':
          return (r.status === 'duplicate');
        default:
          return true;
      }
    });
  };

  const getFilteredCamps = () => {
    return campReports.filter(c => {
      if (failedOnly && !hasFailedNotification(c)) return false;
      const isActive = c.active !== false;
      if (hideResolved && !isActive) return false;
      return true;
    });
  };

  if (!user) {
    return (
      <div className="min-h-screen flex flex-col bg-charcoal-dark text-slate-200">
        <Header />
        <main id="main-content" className="flex-grow flex items-center justify-center px-4">
          <p className="text-slate-400 font-medium uppercase tracking-wider text-xs" role="status">
            Loading outreach dashboard…
          </p>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-charcoal-dark text-slate-200">
      <Header />
      
      <main id="main-content" className="flex-grow max-w-7xl mx-auto w-full px-4 md:px-8 py-8">
        
        {/* Header bar */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4 border-b pb-4 border-white/5">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-extrabold text-white tracking-tight uppercase font-display">Admin Dashboard</h1>
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-3xs font-bold uppercase tracking-wider ${
                isCachedData ? 'bg-yellow-500/10 border border-yellow-500/30 text-yellow-400' : 'bg-green-500/10 border border-green-500/30 text-green-400'
              }`}>
                <span className={`w-1.5 h-1.5 rounded-full ${isCachedData ? 'bg-yellow-500' : 'bg-green-500 animate-pulse'}`} />
                {isCachedData ? 'Offline Cache' : 'Live Sync'}
              </span>
            </div>
            <p className="text-xs text-slate-400 uppercase tracking-wider">Logistics portal for Not By Chance outreach staff</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => navigate('/')}
              className="bg-white/5 hover:bg-white/10 text-white font-bold py-2 px-4 rounded-lg transition-colors text-xs uppercase border border-white/5"
            >
              View Public Site
            </button>
            <button
              onClick={handleLogout}
              className="bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 font-bold py-2 px-4 rounded-lg transition-colors text-xs uppercase border border-red-500/30"
            >
              Logout
            </button>
          </div>
        </div>

        {/* Dashboard Tabs navigation */}
        <div className="flex flex-wrap gap-2 mb-6 border-b border-white/5" role="tablist" aria-label="Admin sections">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'submissions'}
            onClick={() => setActiveTab('submissions')}
            className={`px-4 py-2.5 font-bold transition-all text-xs uppercase tracking-wider whitespace-nowrap ${
              activeTab === 'submissions'
                ? 'border-b-2 border-banana-yellow text-banana-yellow'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Submissions Queue
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'resources'}
            onClick={() => setActiveTab('resources')}
            className={`px-4 py-2.5 font-bold transition-all text-xs uppercase tracking-wider whitespace-nowrap ${
              activeTab === 'resources'
                ? 'border-b-2 border-banana-yellow text-banana-yellow'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Resource Directory ({resources.length})
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'needs'}
            onClick={() => setActiveTab('needs')}
            className={`px-4 py-2.5 font-bold transition-all text-xs uppercase tracking-wider whitespace-nowrap ${
              activeTab === 'needs'
                ? 'border-b-2 border-banana-yellow text-banana-yellow'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Current Needs ({needs.length})
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'events'}
            onClick={() => setActiveTab('events')}
            className={`px-4 py-2.5 font-bold transition-all text-xs uppercase tracking-wider whitespace-nowrap ${
              activeTab === 'events'
                ? 'border-b-2 border-banana-yellow text-banana-yellow'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Upcoming Events ({events.length})
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'map'}
            onClick={() => setActiveTab('map')}
            className={`px-4 py-2.5 font-bold transition-all text-xs uppercase tracking-wider whitespace-nowrap ${
              activeTab === 'map'
                ? 'border-b-2 border-banana-yellow text-banana-yellow'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Outreach Map View
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'settings'}
            onClick={() => setActiveTab('settings')}
            className={`px-4 py-2.5 font-bold transition-all text-xs uppercase tracking-wider whitespace-nowrap ${
              activeTab === 'settings'
                ? 'border-b-2 border-banana-yellow text-banana-yellow'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Availability Controls
          </button>
        </div>

        {/* SUBMISSIONS TAB CONTENT */}
        {activeTab === 'submissions' && (
          <div className="space-y-6">

            {(() => {
              const failedNotifs = [
                ...helpRequests.filter((r) => String(r.notificationStatus || '').startsWith('failed')),
                ...campReports.filter((r) => String(r.notificationStatus || '').startsWith('failed')),
                ...helpOffers.filter((r) => String(r.notificationStatus || '').startsWith('failed')),
              ];
              if (failedNotifs.length === 0) return null;
              return (
                <div className="bg-red-50 border border-red-200 text-red-900 p-4 rounded-lg text-sm leading-relaxed" role="status">
                  <p className="font-bold uppercase text-xs tracking-wider mb-1">
                    Notification delivery issues ({failedNotifs.length})
                  </p>
                  <p>
                    One or more submissions did not email the administrator. Check SMTP secrets
                    (<code className="mx-1 text-xs bg-red-100 px-1 rounded">SMTP_HOST</code>
                    and related values in Firebase Secret Manager, then redeploy functions.
                    Submissions are still saved in Firestore — review the queue below.
                  </p>
                </div>
              );
            })()}
            
            {/* Triage filters controls */}
            <div className="chrome-panel border border-white/5 bg-charcoal-card/60 p-4 rounded-lg flex flex-col md:flex-row gap-4 items-start md:items-center justify-between text-slate-200">
              <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
                <label htmlFor="triageSelect" className="text-xs font-bold text-slate-400 uppercase">
                  Triage Queue View:
                </label>
                <select
                  id="triageSelect"
                  value={triageQueue}
                  onChange={(e) => setTriageQueue(e.target.value)}
                  className="bg-charcoal-dark border border-white/10 text-white rounded px-3 py-1.5 text-xs font-bold focus:ring-2 focus:ring-banana-yellow focus:border-transparent"
                >
                  <option value="all" className="bg-charcoal-card">All Open Requests</option>
                  <option value="new" className="bg-charcoal-card">New / Unread</option>
                  <option value="needs_review" className="bg-charcoal-card">Needs Review</option>
                  <option value="urgent" className="bg-charcoal-card">🚨 Urgent Concern / Immediate Safety</option>
                  <option value="contact_requested" className="bg-charcoal-card">📞 Contact Requested</option>
                  <option value="waiting_supplies" className="bg-charcoal-card">📦 Waiting on Supplies</option>
                  <option value="referral_needed" className="bg-charcoal-card">📋 Referral / Info Needed</option>
                  <option value="outreach_planned" className="bg-charcoal-card">🚗 Outreach Planned</option>
                  <option value="completed" className="bg-charcoal-card">✓ Completed</option>
                  <option value="unable_to_assist" className="bg-charcoal-card">Unable to Assist</option>
                  <option value="closed" className="bg-charcoal-card">Closed</option>
                  <option value="spam" className="bg-charcoal-card">Spam</option>
                  <option value="duplicate" className="bg-charcoal-card">Duplicate</option>
                </select>
              </div>

              <div className="flex flex-wrap gap-4 w-full md:w-auto">
                <label className="inline-flex items-center gap-2 text-xs font-bold text-slate-300 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={hideResolved}
                    onChange={(e) => setHideResolved(e.target.checked)}
                    className="w-4 h-4 rounded bg-charcoal-dark border-white/10 text-banana-yellow focus:ring-banana-yellow"
                  />
                  Hide Completed & Closed
                </label>
                <label className="inline-flex items-center gap-2 text-xs font-bold text-red-400 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={failedOnly}
                    onChange={(e) => setFailedOnly(e.target.checked)}
                    className="w-4 h-4 rounded bg-charcoal-dark border-white/10 text-red-600 focus:ring-red-500"
                  />
                  Failed notifications only
                </label>
              </div>
            </div>

            {/* Help Requests Queue */}
            <section className="chrome-panel border border-white/5 bg-charcoal-card/60 rounded-lg overflow-hidden text-slate-200">
              <div className="p-4 bg-charcoal-dark border-b border-white/5 flex justify-between items-center">
                <h3 className="font-bold text-white uppercase tracking-wider text-xs">
                  Help Requests Queue ({getFilteredRequests().length})
                </h3>
                <span className="text-2xs text-slate-400">Newest 100 loaded</span>
              </div>

              {/* Mobile View Card Grid */}
              <div className="block sm:hidden divide-y divide-white/5 p-4 space-y-4">
                {getFilteredRequests().map((req) => (
                  <div key={req.id} className="pt-4 first:pt-0 space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="font-mono font-bold text-xs text-banana-yellow">
                        Ref: {req.referenceNumber || req.id.substring(0, 8).toUpperCase()}
                      </span>
                      <span className="text-2xs text-slate-500">{formatDisplayDateTime(req.timestamp)}</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      <span className={`px-2 py-0.5 rounded-full text-3xs font-bold uppercase ${
                        req.urgency === 'as_soon_as_available' || req.safetyConcern ? 'bg-red-500/15 border border-red-500/35 text-red-400' : 'bg-white/5 border border-white/5 text-slate-400'
                      }`}>
                        {req.urgency === 'as_soon_as_available' ? 'Urgent' : 'Normal'}
                      </span>
                      <span className={`px-2 py-0.5 rounded text-3xs font-bold uppercase ${
                        req.status === 'new' ? 'bg-orange-500 text-white' :
                        req.status === 'resolved' ? 'bg-green-600 text-white' :
                        req.status === 'closed' ? 'bg-white/5 text-slate-400 border border-white/5' : 'bg-yellow-500 text-gray-900'
                      }`}>
                        {req.status || 'new'}
                      </span>
                    </div>
                    <p className="text-xs text-slate-300 font-medium">
                      <strong>Location:</strong> {escapeAndTruncate(req.locationDesc, 120)}
                    </p>
                    <div className="flex justify-between items-center pt-2">
                      <span className="text-3xs text-slate-400 uppercase font-bold">Contact: {req.preferredContact || 'None'}</span>
                      <button
                        onClick={() => setActiveRequestDetail(req)}
                        className="cyber-btn-orange py-1 px-3 text-3xs uppercase font-bold"
                      >
                        Triage & Edit
                      </button>
                    </div>
                  </div>
                ))}
                {getFilteredRequests().length === 0 && (
                  <p className="text-center text-slate-500 py-6 text-xs font-medium">No matching help requests in queue.</p>
                )}
              </div>

              {/* Desktop View Table */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-xs text-left">
                  <thead className="bg-charcoal-dark text-slate-400 uppercase text-3xs tracking-wider border-b border-white/5">
                    <tr>
                      <th className="px-4 py-3">Ref</th>
                      <th className="px-4 py-3">Urgency</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Notify</th>
                      <th className="px-4 py-3">Location</th>
                      <th className="px-4 py-3">Preferred Contact</th>
                      <th className="px-4 py-3">Submitted</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {getFilteredRequests().map((req) => (
                      <tr key={req.id} className="hover:bg-white/5 transition-colors">
                        <td className="px-4 py-3 font-mono font-bold text-xs text-white">{req.referenceNumber || req.id.substring(0, 8).toUpperCase()}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded-full text-2xs font-bold ${
                            req.urgency === 'as_soon_as_available' || req.safetyConcern ? 'bg-red-500/15 border border-red-500/35 text-red-400' : 'bg-white/5 border border-white/5 text-slate-400'
                          }`}>
                            {req.urgency === 'as_soon_as_available' ? 'As soon as available' : 'Normal'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded text-2xs font-bold uppercase ${
                            req.status === 'new' ? 'bg-orange-500 text-white' :
                            req.status === 'resolved' ? 'bg-green-600 text-white' :
                            req.status === 'closed' ? 'bg-white/5 text-slate-400 border border-white/5' : 'bg-yellow-500 text-gray-900'
                          }`}>
                            {req.status || 'new'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded text-2xs font-bold uppercase ${
                            String(req.notificationStatus || '').startsWith('failed') ? 'bg-red-500/15 border border-red-500/35 text-red-400' :
                            req.notificationStatus === 'sent' ? 'bg-green-500/15 border border-green-500/35 text-green-400' :
                            'bg-white/5 border border-white/5 text-slate-400'
                          }`} title={req.notificationError || ''}>
                            {req.notificationStatus || 'pending'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-300 font-medium">{escapeAndTruncate(req.locationDesc, 60)}</td>
                        <td className="px-4 py-3 font-bold text-2xs text-slate-400 uppercase">{req.preferredContact || 'None'}</td>
                        <td className="px-4 py-3 text-xs text-slate-500">{formatDisplayDateTime(req.timestamp)}</td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => setActiveRequestDetail(req)}
                            className="bg-white/5 hover:bg-white/10 text-white font-bold py-1.5 px-3 rounded text-2xs border border-white/5 transition-colors"
                          >
                            Triage & Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                    {getFilteredRequests().length === 0 && (
                      <tr>
                        <td colSpan="8" className="p-8 text-center text-slate-500 font-medium">
                          No matching help requests in queue.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Camp Reports Queue */}
            <section className="chrome-panel border border-white/5 bg-charcoal-card/60 rounded-lg overflow-hidden text-slate-200">
              <div className="p-4 bg-charcoal-dark border-b border-white/5 flex justify-between items-center">
                <h3 className="font-bold text-white uppercase tracking-wider text-xs">
                  Active Camp Locations ({getFilteredCamps().length})
                </h3>
              </div>

              {/* Mobile View Card Grid */}
              <div className="block sm:hidden divide-y divide-white/5 p-4 space-y-4">
                {getFilteredCamps().map((camp) => (
                  <div key={camp.id} className="pt-4 first:pt-0 space-y-2">
                    <div className="flex justify-between items-center">
                      <span className={`px-2 py-0.5 rounded text-3xs font-bold uppercase ${
                        camp.active !== false ? 'bg-green-500/15 border border-green-500/35 text-green-400' : 'bg-white/5 text-slate-400'
                      }`}>
                        {camp.active !== false ? 'Active' : 'Inactive'}
                      </span>
                      <span className="text-2xs text-slate-500">{formatDisplayDateTime(camp.timestamp)}</span>
                    </div>
                    <p className="text-xs text-slate-300 font-medium">
                      {escapeAndTruncate(camp.description, 120)}
                    </p>
                    <div className="text-3xs text-slate-400 space-y-0.5">
                      <p><strong>People Est:</strong> {camp.peopleCount || 'N/A'}</p>
                      <p><strong>Children:</strong> {camp.childrenObserved || 'Unknown'}</p>
                      <p><strong>Spoke with Camp:</strong> {camp.spokeWithAnyone ? 'Yes' : 'No'}</p>
                    </div>
                    <div className="flex justify-end pt-2">
                      <button
                        onClick={() => setActiveCampDetail(camp)}
                        className="cyber-btn-orange py-1 px-3 text-3xs uppercase font-bold"
                      >
                        Review & Edit
                      </button>
                    </div>
                  </div>
                ))}
                {getFilteredCamps().length === 0 && (
                  <p className="text-center text-slate-500 py-6 text-xs font-medium">No active camp reports.</p>
                )}
              </div>

              {/* Desktop View Table */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-xs text-left">
                  <thead className="bg-charcoal-dark text-slate-400 uppercase text-3xs tracking-wider border-b border-white/5">
                    <tr>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Notify</th>
                      <th className="px-4 py-3">Location / Description</th>
                      <th className="px-4 py-3">People Est.</th>
                      <th className="px-4 py-3">Children</th>
                      <th className="px-4 py-3">Spoke with Camp</th>
                      <th className="px-4 py-3">Submitted</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {getFilteredCamps().map((camp) => (
                      <tr key={camp.id} className="hover:bg-white/5 transition-colors">
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded text-2xs font-bold uppercase ${
                            camp.active !== false ? 'bg-green-500/15 border border-green-500/35 text-green-400' : 'bg-white/5 border border-white/5 text-slate-500'
                          }`}>
                            {camp.active !== false ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded text-2xs font-bold uppercase ${
                            String(camp.notificationStatus || '').startsWith('failed') ? 'bg-red-500/15 border border-red-500/35 text-red-400' :
                            camp.notificationStatus === 'sent' ? 'bg-green-500/15 border border-green-500/35 text-green-400' :
                            'bg-white/5 border border-white/5 text-slate-400'
                          }`} title={camp.notificationError || ''}>
                            {camp.notificationStatus || 'pending'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-300 font-medium">{escapeAndTruncate(camp.description, 80)}</td>
                        <td className="px-4 py-3 font-semibold text-white">{camp.peopleCount || 'N/A'}</td>
                        <td className="px-4 py-3 font-medium text-xs uppercase text-slate-400">{camp.childrenObserved || 'Unknown'}</td>
                        <td className="px-4 py-3 font-medium text-xs uppercase text-slate-400">{camp.spokeWithAnyone ? 'Yes' : 'No'}</td>
                        <td className="px-4 py-3 text-xs text-slate-500">{formatDisplayDateTime(camp.timestamp)}</td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => setActiveCampDetail(camp)}
                            className="bg-white/5 hover:bg-white/10 text-white font-bold py-1.5 px-3 rounded text-2xs border border-white/5 transition-colors"
                          >
                            Review & Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                    {getFilteredCamps().length === 0 && (
                      <tr>
                        <td colSpan="8" className="p-8 text-center text-slate-500 font-medium">
                          No active camp reports.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Volunteer / Help Offers Queue */}
            <section className="chrome-panel border border-white/5 bg-charcoal-card/60 rounded-lg overflow-hidden text-slate-200">
              <div className="p-4 bg-charcoal-dark border-b border-white/5 flex justify-between items-center">
                <h3 className="font-bold text-white uppercase tracking-wider text-xs">
                  Volunteer &amp; Help Offers ({getFilteredOffers().length})
                </h3>
                <span className="text-2xs text-slate-400 hidden md:inline">
                  Public form submissions — contact only for outreach coordination
                </span>
              </div>

              {/* Mobile View Card Grid */}
              <div className="block sm:hidden divide-y divide-white/5 p-4 space-y-4">
                {getFilteredOffers().map((offer) => (
                  <div key={offer.id} className="pt-4 first:pt-0 space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-xs text-white">
                        {offer.name || <span className="text-slate-500 italic">Anonymous</span>}
                      </span>
                      <span className="text-2xs text-slate-500">{formatDisplayDateTime(offer.timestamp)}</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      <span className={`px-2 py-0.5 rounded text-3xs font-bold uppercase ${
                        (offer.status || 'new') === 'new' ? 'bg-orange-500 text-white' :
                        (offer.status === 'completed') ? 'bg-green-600 text-white' :
                        (offer.status === 'declined') ? 'bg-white/5 text-slate-400 border border-white/5' :
                        'bg-yellow-500 text-gray-900'
                      }`}>
                        {offer.status || 'new'}
                      </span>
                    </div>
                    <div className="text-3xs text-slate-400 space-y-0.5">
                      <p><strong>Offer types:</strong> {Array.isArray(offer.types) ? offer.types.join(', ') : '—'}</p>
                      <p><strong>Contact:</strong> {offer.email || offer.phone || 'None'}</p>
                      <p><strong>Event:</strong> {offer.eventName || '—'}</p>
                    </div>
                    <div className="flex justify-end pt-2">
                      <button
                        type="button"
                        onClick={() => setActiveOfferDetail(offer)}
                        className="cyber-btn-orange py-1 px-3 text-3xs uppercase font-bold"
                      >
                        Review
                      </button>
                    </div>
                  </div>
                ))}
                {getFilteredOffers().length === 0 && (
                  <p className="text-center text-slate-500 py-6 text-xs font-medium">No volunteer offers in queue.</p>
                )}
              </div>

              {/* Desktop View Table */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-xs text-left">
                  <thead className="bg-charcoal-dark text-slate-400 uppercase text-3xs tracking-wider border-b border-white/5">
                    <tr>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Notify</th>
                      <th className="px-4 py-3">Name</th>
                      <th className="px-4 py-3">Offer types</th>
                      <th className="px-4 py-3">Contact</th>
                      <th className="px-4 py-3">Event</th>
                      <th className="px-4 py-3">Submitted</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {getFilteredOffers().map((offer) => (
                      <tr key={offer.id} className="hover:bg-white/5 transition-colors">
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded text-2xs font-bold uppercase ${
                            (offer.status || 'new') === 'new' ? 'bg-orange-500 text-white' :
                            (offer.status === 'completed') ? 'bg-green-600 text-white' :
                            (offer.status === 'declined') ? 'bg-white/5 text-slate-400 border border-white/5' :
                            'bg-yellow-500 text-gray-900'
                          }`}>
                            {offer.status || 'new'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded text-2xs font-bold uppercase ${
                            String(offer.notificationStatus || '').startsWith('failed') ? 'bg-red-500/15 border border-red-500/35 text-red-400' :
                            offer.notificationStatus === 'sent' ? 'bg-green-500/15 border border-green-500/35 text-green-400' :
                            'bg-white/5 border border-white/5 text-slate-400'
                          }`} title={offer.notificationError || ''}>
                            {offer.notificationStatus || 'pending'}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-medium text-white">
                          {offer.name || <span className="text-slate-500 italic">Anonymous</span>}
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-300">
                          {Array.isArray(offer.types) ? offer.types.join(', ') : '—'}
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-300">
                          {offer.email || offer.phone || <span className="text-slate-500">None</span>}
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-400">
                          {offer.eventName || '—'}
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-500">{formatDisplayDateTime(offer.timestamp)}</td>
                        <td className="px-4 py-3 text-right">
                          <button
                            type="button"
                            onClick={() => setActiveOfferDetail(offer)}
                            className="bg-white/5 hover:bg-white/10 text-white font-bold py-1.5 px-3 rounded text-2xs border border-white/5 transition-colors"
                          >
                            Review
                          </button>
                        </td>
                      </tr>
                    ))}
                    {getFilteredOffers().length === 0 && (
                      <tr>
                        <td colSpan="8" className="p-8 text-center text-slate-500 font-medium">
                          No volunteer offers in queue.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

          </div>
        )}

        {/* DETAILED HELP REQUEST TRIAGE MODAL */}
        {activeRequestDetail && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 animate-fadeIn">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                <div>
                  <span className="text-2xs font-bold uppercase text-urgent-orange tracking-wider bg-orange-100 px-2 py-0.5 rounded">Triage Case File</span>
                  <h3 className="text-xl font-bold text-gray-900 mt-1">Ref: {activeRequestDetail.referenceNumber || activeRequestDetail.id.substring(0, 8).toUpperCase()}</h3>
                </div>
                <button
                  onClick={() => setActiveRequestDetail(null)}
                  className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                >
                  &times;
                </button>
              </div>

              <div className="p-6 space-y-6">
                
                {/* Deletion Warning Banner */}
                {activeRequestDetail.deleteAfter && (
                  <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded-lg text-xs leading-relaxed">
                    ⚠️ <strong>Privacy retention deletion scheduled:</strong> This resolved document and all associated precise locations, notes, and contact methods will be permanently deleted on <strong>{activeRequestDetail.deleteAfter.toDate().toLocaleDateString()}</strong>.
                  </div>
                )}

                {/* Grid Info details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm bg-gray-50 p-4 rounded-lg">
                  <div>
                    <p className="text-xs text-gray-400 uppercase font-bold">Submission Details</p>
                    <p className="mt-1"><strong>Submitted:</strong> {formatDisplayDateTime(activeRequestDetail.timestamp)}</p>
                    <p><strong>Urgency:</strong> {activeRequestDetail.urgency}</p>
                    <p><strong>Preferred contact:</strong> {activeRequestDetail.preferredContact}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 uppercase font-bold">Household Profile</p>
                    <p className="mt-1"><strong>Adults:</strong> {activeRequestDetail.adultsCount || 1}</p>
                    <p><strong>Children:</strong> {activeRequestDetail.childrenCount || 0}</p>
                    <p><strong>Pets present:</strong> {activeRequestDetail.petsPresent ? 'Yes' : 'No'}</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="text-xs text-gray-400 uppercase font-bold">Location Coordinates & Landing Mark</p>
                  <p><strong>Landmark:</strong> {activeRequestDetail.landmark || 'N/A'}</p>
                  <p className="text-gray-700 bg-gray-100 p-2.5 rounded font-mono text-xs whitespace-pre-wrap">{activeRequestDetail.locationDesc}</p>
                  {activeRequestDetail.latitude != null && activeRequestDetail.longitude != null && (
                    <div className="flex gap-2 items-center mt-2">
                      <span className="text-xs text-hope-green font-bold">📍 {activeRequestDetail.latitude.toFixed(6)}, {activeRequestDetail.longitude.toFixed(6)}</span>
                      <button
                        onClick={() => copyText(`${activeRequestDetail.latitude}, ${activeRequestDetail.longitude}`, 'Coordinates')}
                        className="text-2xs bg-trust-blue text-white px-2 py-0.5 rounded font-bold uppercase hover:bg-blue-600"
                      >
                        Copy coords
                      </button>
                    </div>
                  )}
                </div>

                {/* Categories Needed */}
                <div>
                  <p className="text-xs text-gray-400 uppercase font-bold mb-2">Requested Supplies Categories</p>
                  <div className="flex flex-wrap gap-1.5">
                    {activeRequestDetail.requestedCategories && activeRequestDetail.requestedCategories.map((cat) => (
                      <span key={cat} className="bg-trust-blue/10 text-trust-blue font-bold text-xs px-2.5 py-1 rounded">
                        {cat}
                      </span>
                    ))}
                    {(!activeRequestDetail.requestedCategories || activeRequestDetail.requestedCategories.length === 0) && (
                      <span className="text-gray-400">None checked</span>
                    )}
                  </div>
                  {activeRequestDetail.clothingSizes && (
                    <p className="text-xs mt-2"><strong>Clothing sizes:</strong> {activeRequestDetail.clothingSizes}</p>
                  )}
                </div>

                {/* Contact Permissions checks */}
                <div className="border-t pt-4">
                  <p className="text-xs text-gray-400 uppercase font-bold mb-2">Contact Details & Permissions</p>
                  <div className="space-y-1">
                    {activeRequestDetail.phone && (
                      <p><strong>Phone:</strong> {activeRequestDetail.phone} (Text permission: {activeRequestDetail.permissionToText ? 'Yes' : 'No'})</p>
                    )}
                    {activeRequestDetail.email && (
                      <p><strong>Email:</strong> {activeRequestDetail.email}</p>
                    )}
                    <p><strong>Outreach worker approach permission:</strong> {activeRequestDetail.permissionToApproach ? 'Yes' : 'No'}</p>
                    <p><strong>Best time to visit:</strong> {activeRequestDetail.bestTimeToVisit || 'Anytime'}</p>
                  </div>
                </div>

                {/* Triage / Update form edits */}
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    const notes = e.target.adminNotes.value;
                    const itemsDelivered = e.target.itemsDelivered.value;
                    const referralInfo = e.target.referralInfo.value;
                    const assignedTo = e.target.assignedTo.value;
                    
                    handleSaveRequestEdits(activeRequestDetail.id, {
                      adminNotes: notes.trim(),
                      itemsDelivered: itemsDelivered.trim(),
                      referralInfo: referralInfo.trim(),
                      assignedTo: assignedTo.trim()
                    });
                  }}
                  className="space-y-4 border-t pt-4"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="assignedTo" className="block text-xs font-bold text-gray-500 uppercase mb-1">Assign Outreach Staff</label>
                      <input
                        type="text"
                        id="assignedTo"
                        name="assignedTo"
                        defaultValue={activeRequestDetail.assignedTo || ''}
                        className="w-full border border-gray-300 rounded p-2 text-sm focus:ring-2 focus:ring-trust-blue"
                        placeholder="Staff Name / Volunteer Group"
                      />
                    </div>

                    <div>
                      <label htmlFor="itemsDelivered" className="block text-xs font-bold text-gray-500 uppercase mb-1">Delivered supplies log</label>
                      <input
                        type="text"
                        id="itemsDelivered"
                        name="itemsDelivered"
                        defaultValue={activeRequestDetail.itemsDelivered || ''}
                        className="w-full border border-gray-300 rounded p-2 text-sm focus:ring-2 focus:ring-trust-blue"
                        placeholder="e.g. 1 tent, 2 blankets"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="referralInfo" className="block text-xs font-bold text-gray-500 uppercase mb-1">Active referall information logged</label>
                    <input
                      type="text"
                      id="referralInfo"
                      name="referralInfo"
                      defaultValue={activeRequestDetail.referralInfo || ''}
                      className="w-full border border-gray-300 rounded p-2 text-sm focus:ring-2 focus:ring-trust-blue"
                      placeholder="Organization referred to / phone info shared"
                    />
                  </div>

                  <div>
                    <label htmlFor="adminNotes" className="block text-xs font-bold text-gray-500 uppercase mb-1">Administrative / Case Triage Notes</label>
                    <textarea
                      id="adminNotes"
                      name="adminNotes"
                      defaultValue={activeRequestDetail.adminNotes || ''}
                      rows="3"
                      className="w-full border border-gray-300 rounded p-2 text-sm focus:ring-2 focus:ring-trust-blue"
                      placeholder="Add private administrative updates here..."
                    />
                  </div>

                  {/* Notification delivery logs */}
                  <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded">
                    <p><strong>Notification Logs:</strong></p>
                    <p>Status: <span className="font-semibold">{activeRequestDetail.notificationStatus || 'pending'}</span></p>
                    {activeRequestDetail.notificationError && <p className="text-red-500">Error: {activeRequestDetail.notificationError}</p>}
                    {activeRequestDetail.notificationSentAt && <p>Sent at: {formatDisplayDateTime(activeRequestDetail.notificationSentAt)}</p>}
                  </div>

                  <div className="flex flex-wrap gap-2 pt-2">
                    <button
                      type="submit"
                      className="bg-hope-green hover:bg-green-600 text-white font-bold py-2 px-4 rounded transition text-sm"
                    >
                      Save Case Edits
                    </button>

                    <select
                      value={activeRequestDetail.status || 'new'}
                      onChange={(e) => updateStatus('help_requests', activeRequestDetail.id, e.target.value)}
                      className="border border-gray-300 rounded px-3 py-1.5 text-sm font-bold bg-white text-gray-800"
                    >
                      <option value="new">Mark New / Unread</option>
                      <option value="needs_review">Mark Needs Review</option>
                      <option value="in_progress">Mark In Progress</option>
                      <option value="waiting_supplies">Mark Waiting Supplies</option>
                      <option value="referral_needed">Mark Referral Needed</option>
                      <option value="outreach_planned">Mark Outreach Planned</option>
                      <option value="resolved">Mark Resolved (starts 30-day retention)</option>
                      <option value="closed">Mark Closed (starts 30-day retention)</option>
                      <option value="unable_to_assist">Mark Unable to Assist</option>
                      <option value="spam">Mark Spam</option>
                      <option value="duplicate">Mark Duplicate</option>
                    </select>

                    <button
                      type="button"
                      onClick={() => handleDeleteSubmission('help_requests', activeRequestDetail.id, 'help request')}
                      className="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded transition text-sm ml-auto"
                    >
                      Immediate Delete
                    </button>
                  </div>

                </form>

              </div>
            </div>
          </div>
        )}

        {/* DETAILED CAMP REPORT REVIEW MODAL */}
        {activeCampDetail && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 animate-fadeIn">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                <div>
                  <span className="text-2xs font-bold uppercase text-accent-cyan tracking-wider bg-cyan-100 px-2 py-0.5 rounded">Camp File</span>
                  <h3 className="text-xl font-bold text-gray-900 mt-1">Review Camp Report</h3>
                </div>
                <button
                  onClick={() => setActiveCampDetail(null)}
                  className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                >
                  &times;
                </button>
              </div>

              <div className="p-6 space-y-6">
                <div>
                  <p className="text-xs text-gray-400 uppercase font-bold mb-1">Submitted Description</p>
                  <p className="text-gray-700 bg-gray-50 p-3 rounded text-sm whitespace-pre-wrap">{activeCampDetail.description}</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm bg-gray-50 p-4 rounded-lg">
                  <div>
                    <p className="text-xs text-gray-400 uppercase font-bold">Observations</p>
                    <p className="mt-1"><strong>Est. Size:</strong> {activeCampDetail.peopleCount || 'N/A'}</p>
                    <p><strong>Children:</strong> {activeCampDetail.childrenObserved || 'Unknown'}</p>
                    <p><strong>Pets:</strong> {activeCampDetail.petsObserved || 'Unknown'}</p>
                    <p><strong>Spoke with residents:</strong> {activeCampDetail.spokeWithAnyone ? 'Yes' : 'No'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 uppercase font-bold">Reporter & Details</p>
                    <p className="mt-1"><strong>Observed Date:</strong> {activeCampDetail.dateObserved || 'N/A'}</p>
                    <p><strong>Observed Time:</strong> {activeCampDetail.timeObserved || 'N/A'}</p>
                    {activeCampDetail.phone && <p><strong>Phone:</strong> {activeCampDetail.phone}</p>}
                    {activeCampDetail.email && <p><strong>Email:</strong> {activeCampDetail.email}</p>}
                  </div>
                </div>

                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    try {
                      await updateDoc(doc(db, 'camp_reports', activeCampDetail.id), {
                        campNeeds: e.target.campNeeds.value.trim() || null,
                        peopleContacted: e.target.peopleContacted.value
                          ? parseInt(e.target.peopleContacted.value, 10)
                          : null,
                        peopleCount: parseInt(e.target.peopleCount.value, 10) || null,
                        active: e.target.campActive.value === 'true',
                        updatedAt: serverTimestamp(),
                      });
                      setToast({ message: 'Camp updated successfully!', type: 'success' });
                      setActiveCampDetail(null);
                    } catch (err) {
                      setToast({ message: 'Error saving camp.', type: 'error' });
                    }
                  }}
                  className="space-y-4 border-t pt-4"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="peopleCount" className="block text-xs font-bold text-gray-500 uppercase mb-1">Confirm Number of People</label>
                      <input
                        type="number"
                        id="peopleCount"
                        name="peopleCount"
                        defaultValue={activeCampDetail.peopleCount || ''}
                        className="w-full border border-gray-300 rounded p-2 text-sm"
                      />
                    </div>
                    <div>
                      <label htmlFor="campActive" className="block text-xs font-bold text-gray-500 uppercase mb-1">Active Status</label>
                      <select
                        id="campActive"
                        name="campActive"
                        defaultValue={activeCampDetail.active !== false ? 'true' : 'false'}
                        className="w-full border border-gray-300 rounded p-2 text-sm font-semibold"
                      >
                        <option value="true">Active Location</option>
                        <option value="false">Inactive / Cleared Location</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="campNeeds" className="block text-xs font-bold text-gray-500 uppercase mb-1">Identified Camp Needs</label>
                    <textarea
                      id="campNeeds"
                      name="campNeeds"
                      defaultValue={activeCampDetail.campNeeds || ''}
                      rows="2"
                      className="w-full border border-gray-300 rounded p-2 text-sm"
                      placeholder="e.g. Tents, tarps, winter jackets"
                    />
                  </div>

                  <div>
                    <label htmlFor="peopleContacted" className="block text-xs font-bold text-gray-500 uppercase mb-1">
                      People Contacted (count only — do not store names)
                    </label>
                    <input
                      type="number"
                      id="peopleContacted"
                      name="peopleContacted"
                      min="0"
                      max="100"
                      defaultValue={activeCampDetail.peopleContacted ?? ''}
                      className="w-full border border-gray-300 rounded p-2 text-sm"
                      placeholder="e.g. 2"
                    />
                  </div>

                  <div className="flex gap-2 pt-2">
                    <button
                      type="submit"
                      className="bg-hope-green hover:bg-green-600 text-white font-bold py-2 px-4 rounded text-sm transition"
                    >
                      Save Camp Details
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteSubmission('camp_reports', activeCampDetail.id, 'camp report')}
                      className="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded text-sm transition ml-auto"
                    >
                      Delete Report
                    </button>
                  </div>
                </form>

              </div>
            </div>
          </div>
        )}

        {/* VOLUNTEER OFFER DETAIL MODAL */}
        {activeOfferDetail && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 animate-fadeIn">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                <div>
                  <span className="text-2xs font-bold uppercase text-hope-green tracking-wider bg-green-100 px-2 py-0.5 rounded">
                    Volunteer Offer
                  </span>
                  <h3 className="text-xl font-bold text-gray-900 mt-1">
                    {activeOfferDetail.name || 'Anonymous offer'}
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() => setActiveOfferDetail(null)}
                  className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                  aria-label="Close"
                >
                  &times;
                </button>
              </div>

              <div className="p-6 space-y-5 text-sm">
                {activeOfferDetail.deleteAfter && (
                  <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded-lg text-xs leading-relaxed">
                    ⚠️ <strong>Retention:</strong> scheduled for deletion on{' '}
                    <strong>
                      {activeOfferDetail.deleteAfter.toDate
                        ? activeOfferDetail.deleteAfter.toDate().toLocaleDateString()
                        : 'scheduled date'}
                    </strong>
                    .
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                  <div>
                    <p className="text-xs text-gray-400 uppercase font-bold">Contact</p>
                    <p className="mt-1"><strong>Name:</strong> {activeOfferDetail.name || 'Not provided'}</p>
                    <p><strong>Email:</strong> {activeOfferDetail.email || 'Not provided'}</p>
                    <p><strong>Phone:</strong> {activeOfferDetail.phone || 'Not provided'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 uppercase font-bold">Submission</p>
                    <p className="mt-1"><strong>Submitted:</strong> {formatDisplayDateTime(activeOfferDetail.timestamp)}</p>
                    <p><strong>Event:</strong> {activeOfferDetail.eventName || 'General'}</p>
                    <p><strong>Notification:</strong> {activeOfferDetail.notificationStatus || 'pending'}</p>
                  </div>
                </div>

                <div>
                  <p className="text-xs text-gray-400 uppercase font-bold mb-1">Offer types</p>
                  <p className="text-gray-800 font-medium">
                    {Array.isArray(activeOfferDetail.types) ? activeOfferDetail.types.join(', ') : '—'}
                  </p>
                </div>

                {activeOfferDetail.details && (
                  <div>
                    <p className="text-xs text-gray-400 uppercase font-bold mb-1">Details</p>
                    <p className="text-gray-700 whitespace-pre-wrap bg-gray-50 p-3 rounded">
                      {activeOfferDetail.details}
                    </p>
                  </div>
                )}

                {activeOfferDetail.specificHelp && (
                  <div>
                    <p className="text-xs text-gray-400 uppercase font-bold mb-1">Event-specific help</p>
                    <p className="text-gray-700 whitespace-pre-wrap bg-gray-50 p-3 rounded">
                      {activeOfferDetail.specificHelp}
                    </p>
                  </div>
                )}

                <div className="border-t pt-4 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                  <div className="flex items-center gap-2">
                    <label htmlFor="offerStatus" className="text-xs font-bold text-gray-500 uppercase">
                      Status
                    </label>
                    <select
                      id="offerStatus"
                      value={activeOfferDetail.status || 'new'}
                      onChange={(e) => updateStatus('help_offers', activeOfferDetail.id, e.target.value)}
                      className="border border-gray-300 rounded p-2 text-sm font-semibold"
                    >
                      <option value="new">New</option>
                      <option value="contacted">Contacted</option>
                      <option value="scheduled">Scheduled</option>
                      <option value="completed">Completed</option>
                      <option value="declined">Declined</option>
                    </select>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDeleteSubmission('help_offers', activeOfferDetail.id, 'volunteer offer')}
                    className="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded text-sm transition"
                  >
                    Delete offer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* RESOURCE DIRECTORY TAB CONTENT */}
        {activeTab === 'resources' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-white uppercase tracking-tight font-display">Resource Directory & referrals</h2>
              <button
                onClick={() => {
                  setEditingResource(null);
                  setResourceForm({ name: '', categories: '', phone: '', website: '', serviceArea: '', hours: '', eligibility: '', publicDescription: '', notes: '', active: true });
                  setShowResourceForm(true);
                }}
                className="cyber-btn-orange py-2 px-4 text-xs uppercase font-bold"
              >
                + Add Resource Organization
              </button>
            </div>

            {showResourceForm && (
              <div className="chrome-panel border border-white/5 bg-charcoal-card p-6 mb-6 rounded-lg text-slate-200">
                <h3 className="font-bold text-lg mb-4 text-white uppercase tracking-tight font-display">{editingResource ? 'Edit Resource' : 'Add Resource Organization'}</h3>
                <form onSubmit={handleResourceSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="org-name" className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Organization Name *</label>
                      <input
                        id="org-name"
                        type="text"
                        value={resourceForm.name}
                        onChange={(e) => setResourceForm({...resourceForm, name: e.target.value})}
                        required
                        className="w-full bg-charcoal-dark border border-white/10 rounded p-2 text-sm text-white focus:ring-2 focus:ring-banana-yellow focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label htmlFor="org-categories" className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Categories (comma separated) *</label>
                      <input
                        id="org-categories"
                        type="text"
                        value={resourceForm.categories}
                        onChange={(e) => setResourceForm({...resourceForm, categories: e.target.value})}
                        placeholder="e.g. Shelter, Food, Medical"
                        required
                        className="w-full bg-charcoal-dark border border-white/10 rounded p-2 text-sm text-white focus:ring-2 focus:ring-banana-yellow focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="org-phone" className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Phone Number</label>
                      <input
                        id="org-phone"
                        type="tel"
                        value={resourceForm.phone}
                        onChange={(e) => setResourceForm({...resourceForm, phone: e.target.value})}
                        className="w-full bg-charcoal-dark border border-white/10 rounded p-2 text-sm text-white focus:ring-2 focus:ring-banana-yellow focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label htmlFor="org-website" className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Website URL</label>
                      <input
                        id="org-website"
                        type="url"
                        value={resourceForm.website}
                        onChange={(e) => setResourceForm({...resourceForm, website: e.target.value})}
                        className="w-full bg-charcoal-dark border border-white/10 rounded p-2 text-sm text-white focus:ring-2 focus:ring-banana-yellow focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="org-area" className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Service Area</label>
                      <input
                        id="org-area"
                        type="text"
                        value={resourceForm.serviceArea}
                        onChange={(e) => setResourceForm({...resourceForm, serviceArea: e.target.value})}
                        className="w-full bg-charcoal-dark border border-white/10 rounded p-2 text-sm text-white focus:ring-2 focus:ring-banana-yellow focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label htmlFor="org-hours" className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Hours of Operation</label>
                      <input
                        id="org-hours"
                        type="text"
                        value={resourceForm.hours}
                        onChange={(e) => setResourceForm({...resourceForm, hours: e.target.value})}
                        className="w-full bg-charcoal-dark border border-white/10 rounded p-2 text-sm text-white focus:ring-2 focus:ring-banana-yellow focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="org-eligibility" className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Eligibility Details</label>
                    <input
                      id="org-eligibility"
                      type="text"
                      value={resourceForm.eligibility}
                      onChange={(e) => setResourceForm({...resourceForm, eligibility: e.target.value})}
                      className="w-full bg-charcoal-dark border border-white/10 rounded p-2 text-sm text-white focus:ring-2 focus:ring-banana-yellow focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label htmlFor="org-public-description" className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">
                      Public Description <span className="text-banana-yellow">(shown on the website)</span>
                    </label>
                    <textarea
                      id="org-public-description"
                      value={resourceForm.publicDescription}
                      onChange={(e) => setResourceForm({...resourceForm, publicDescription: e.target.value})}
                      rows="2"
                      placeholder="Plain language, what a person will actually get here."
                      className="w-full bg-charcoal-dark border border-white/10 rounded p-2 text-sm text-white focus:ring-2 focus:ring-banana-yellow focus:border-transparent resize-none"
                    />
                    <p className="text-[11px] text-slate-500 mt-1">
                      This is the only description the public sees. Internal notes below are never published.
                    </p>
                  </div>

                  <div>
                    <label htmlFor="org-notes" className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Internal Reference Notes <span className="text-slate-500">(staff only, never published)</span></label>
                    <textarea
                      id="org-notes"
                      value={resourceForm.notes}
                      onChange={(e) => setResourceForm({...resourceForm, notes: e.target.value})}
                      rows="2"
                      className="w-full bg-charcoal-dark border border-white/10 rounded p-2 text-sm text-white focus:ring-2 focus:ring-banana-yellow focus:border-transparent resize-none"
                    />
                  </div>

                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={resourceForm.active}
                      onChange={(e) => setResourceForm({...resourceForm, active: e.target.checked})}
                      className="w-4 h-4 text-banana-yellow border-white/10 rounded bg-charcoal-dark focus:ring-banana-yellow"
                    />
                    <span className="text-sm font-semibold text-slate-200">Active referral source</span>
                  </label>

                  <div className="flex gap-2">
                    <button type="submit" className="cyber-btn-orange py-2 px-4 text-xs uppercase font-bold">Save Resource</button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowResourceForm(false);
                        setEditingResource(null);
                      }}
                      className="bg-white/5 hover:bg-white/10 text-white font-bold py-2 px-4 rounded-lg transition-colors text-xs uppercase border border-white/5"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="chrome-panel border border-white/5 rounded-lg bg-charcoal-card/60 divide-y divide-white/5">
              {resources.map((res) => (
                <div key={res.id} className="p-4 flex justify-between items-start hover:bg-white/5 transition-colors">
                  <div>
                    <h4 className="font-bold text-lg text-white">{res.name}</h4>
                    <p className="text-xs text-slate-400">
                      Last Verified: {res.lastVerified ? formatDisplayDate(res.lastVerified) : 'TBD'}
                    </p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {res.categories && res.categories.map(c => (
                        <span key={c} className="bg-white/5 text-slate-300 border border-white/5 text-2xs font-bold px-2 py-0.5 rounded">
                          {c}
                        </span>
                      ))}
                    </div>
                    <div className="text-sm text-slate-300 mt-2 space-y-1">
                      {res.phone && <p><strong>Phone:</strong> {res.phone}</p>}
                      {res.website && <p><strong>Website:</strong> <a href={res.website} target="_blank" rel="noreferrer" className="text-accent-cyan hover:underline">{res.website}</a></p>}
                      {res.hours && <p><strong>Hours:</strong> {res.hours}</p>}
                      {res.eligibility && <p><strong>Eligibility:</strong> {res.eligibility}</p>}
                      {res.notes && <p className="text-slate-400 italic mt-1">{res.notes}</p>}
                    </div>
                  </div>

                  <div className="flex gap-1">
                    <button
                      onClick={() => {
                        setEditingResource(res);
                        setResourceForm({
                          name: res.name,
                          categories: res.categories ? res.categories.join(', ') : '',
                          phone: res.phone || '',
                          website: res.website || '',
                          serviceArea: res.serviceArea || '',
                          hours: res.hours || '',
                          eligibility: res.eligibility || '',
                          publicDescription: res.publicDescription || '',
                          notes: res.notes || '',
                          active: res.active !== false
                        });
                        setShowResourceForm(true);
                      }}
                      className="bg-white/5 hover:bg-white/10 text-white font-bold py-1.5 px-3 rounded text-xs transition border border-white/5"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteResource(res.id)}
                      className="bg-red-500/10 hover:bg-red-500/20 text-red-400 font-bold py-1.5 px-3 rounded text-xs transition border border-red-500/30"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
              {resources.length === 0 && (
                <p className="p-8 text-center text-slate-500 font-medium">No resources referral records registered yet.</p>
              )}
            </div>
          </div>
        )}

        {/* CURRENT NEEDS TAB CRUD CONTENT */}
        {activeTab === 'needs' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-800">Current Needs Management</h2>
              <button
                onClick={() => {
                  setEditingNeed(null);
                  setNeedForm({ title: '', description: '' });
                  setShowNeedForm(true);
                }}
                className="bg-hope-green hover:bg-green-600 text-white font-bold py-2 px-4 rounded transition"
              >
                + Add Need Item
              </button>
            </div>

            {showNeedForm && (
              <div className="chrome-panel border border-white/5 bg-charcoal-card p-6 mb-6 rounded-lg text-slate-200">
                <h3 className="text-lg font-bold mb-4 text-white uppercase tracking-tight font-display">{editingNeed ? 'Edit Need Item' : 'Add New Need Item'}</h3>
                <form onSubmit={handleNeedSubmit} className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Need Title *</label>
                    <input
                      type="text"
                      value={needForm.title}
                      onChange={(e) => setNeedForm({ ...needForm, title: e.target.value })}
                      required
                      className="w-full bg-charcoal-dark border border-white/10 rounded p-2 text-sm text-white focus:ring-2 focus:ring-banana-yellow focus:border-transparent"
                      placeholder="e.g. Winter tents (4-person)"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Description / Details *</label>
                    <textarea
                      value={needForm.description}
                      onChange={(e) => setNeedForm({ ...needForm, description: e.target.value })}
                      required
                      rows="3"
                      className="w-full bg-charcoal-dark border border-white/10 rounded p-2 text-sm text-white focus:ring-2 focus:ring-banana-yellow focus:border-transparent resize-none"
                      placeholder="Describe sizes, specifications, or drop-off guidelines."
                    />
                  </div>
                  <div className="flex gap-2">
                    <button type="submit" className="cyber-btn-orange py-2 px-4 text-xs uppercase font-bold">
                      {editingNeed ? 'Update' : 'Add'} Need
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowNeedForm(false);
                        setEditingNeed(null);
                      }}
                      className="bg-white/5 hover:bg-white/10 text-white font-bold py-2 px-4 rounded-lg transition-colors text-xs uppercase border border-white/5"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="chrome-panel border border-white/5 rounded-lg bg-charcoal-card/60 divide-y divide-white/5">
              {needs.map((need) => (
                <div key={need.id} className="p-4 sm:p-6 flex justify-between items-start hover:bg-white/5 transition-colors">
                  <div className="flex-grow pr-4">
                    <h4 className="font-bold text-lg text-white">{need.title}</h4>
                    <p className="text-sm text-slate-300 mt-1 whitespace-pre-wrap">{need.description}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setEditingNeed(need);
                        setNeedForm({ title: need.title, description: need.description });
                        setShowNeedForm(true);
                      }}
                      className="bg-white/5 hover:bg-white/10 text-white font-bold py-1.5 px-3 rounded text-xs transition border border-white/5"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteNeed(need.id)}
                      className="bg-red-500/10 hover:bg-red-500/20 text-red-400 font-bold py-1.5 px-3 rounded text-xs transition border border-red-500/30"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
              {needs.length === 0 && (
                <p className="p-8 text-center text-slate-500 font-medium">No need items listed. Add needs items to show on landing page.</p>
              )}
            </div>
          </div>
        )}

        {/* EVENTS TAB CONTENT */}
        {activeTab === 'events' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-white uppercase tracking-tight font-display">Events Management</h2>
              <button
                onClick={() => {
                  setEditingEvent(null);
                  setEventForm({ title: '', description: '', date: '' });
                  setShowEventForm(true);
                }}
                className="cyber-btn-orange py-2 px-4 text-xs uppercase font-bold"
              >
                + Add Upcoming Event
              </button>
            </div>

            {showEventForm && (
              <div className="chrome-panel border border-white/5 bg-charcoal-card p-6 mb-6 rounded-lg text-slate-200">
                <h3 className="text-lg font-bold mb-4 text-white uppercase tracking-tight font-display">{editingEvent ? 'Edit Event Details' : 'Add New Event Details'}</h3>
                <form onSubmit={handleEventSubmit} className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Event Title *</label>
                    <input
                      type="text"
                      value={eventForm.title}
                      onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                      required
                      className="w-full bg-charcoal-dark border border-white/10 rounded p-2 text-sm text-white focus:ring-2 focus:ring-banana-yellow focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Description *</label>
                    <textarea
                      value={eventForm.description}
                      onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                      required
                      rows="3"
                      className="w-full bg-charcoal-dark border border-white/10 rounded p-2 text-sm text-white focus:ring-2 focus:ring-banana-yellow focus:border-transparent resize-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Event Date</label>
                    <input
                      type="date"
                      value={eventForm.date}
                      onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })}
                      className="w-full bg-charcoal-dark border border-white/10 rounded p-2 text-sm text-white focus:ring-2 focus:ring-banana-yellow focus:border-transparent"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button type="submit" className="cyber-btn-orange py-2 px-4 text-xs uppercase font-bold">
                      {editingEvent ? 'Update' : 'Add'} Event
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowEventForm(false);
                        setEditingEvent(null);
                      }}
                      className="bg-white/5 hover:bg-white/10 text-white font-bold py-2 px-4 rounded-lg transition-colors text-xs uppercase border border-white/5"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="chrome-panel border border-white/5 rounded-lg bg-charcoal-card/60 divide-y divide-white/5">
              {events.map((event) => (
                <div key={event.id} className="p-4 sm:p-6 flex justify-between items-start hover:bg-white/5 transition-colors">
                  <div>
                    <h4 className="font-bold text-lg text-white">{event.title}</h4>
                    <p className="text-sm text-slate-300 mt-1 whitespace-pre-wrap">{event.description}</p>
                    {event.date && (
                      <p className="text-xs text-banana-yellow font-bold mt-2">📅 {formatDisplayDate(event.date)}</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setEditingEvent(event);
                        setEventForm({
                          title: event.title,
                          description: event.description,
                          date: event.date ? (event.date.toDate ? event.date.toDate().toISOString().split('T')[0] : event.date) : ''
                        });
                        setShowEventForm(true);
                      }}
                      className="bg-white/5 hover:bg-white/10 text-white font-bold py-1.5 px-3 rounded text-xs transition border border-white/5"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteEvent(event.id)}
                      className="bg-red-500/10 hover:bg-red-500/20 text-red-400 font-bold py-1.5 px-3 rounded text-xs transition border border-red-500/30"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
              {events.length === 0 && (
                <p className="p-8 text-center text-gray-400 font-medium">No outreach events registered yet.</p>
              )}
            </div>
          </div>
        )}

        {/* MAP VIEW TAB */}
        {activeTab === 'map' && (
          <div className="space-y-6 chrome-panel border border-white/5 bg-charcoal-card/60 p-6 rounded-lg text-slate-200">
            <h2 className="text-xl font-bold text-white uppercase tracking-tight font-display">Interactive Map View</h2>
            <MapComponent
              mode="view"
              markers={[
                ...campReports.filter(c => c.active !== false && c.latitude && c.longitude).map(c => ({
                  lat: c.latitude,
                  lng: c.longitude,
                  popupText: `
                    <div style="min-width: 160px;">
                      <span style="background-color: #22c55e; color: white; font-size: 9px; padding: 1.5px 5px; border-radius: 4px; font-weight: bold;">CAMP REPORT</span>
                      <p style="margin: 4px 0; font-size: 11px; color: #444;">${escapeAndTruncate(c.description, 80)}</p>
                    </div>
                  `
                })),
                ...helpRequests.filter(r => r.latitude && r.longitude && (r.status || 'new') !== 'resolved').map(r => ({
                  lat: r.latitude,
                  lng: r.longitude,
                  popupText: `
                    <div style="min-width: 160px;">
                      <span style="background-color: #f97316; color: white; font-size: 9px; padding: 1.5px 5px; border-radius: 4px; font-weight: bold;">HELP REQUEST</span>
                      <p style="margin: 4px 0; font-size: 11px; color: #444;">${escapeAndTruncate(r.needs, 80)}</p>
                    </div>
                  `
                }))
              ]}
            />
          </div>
        )}

        {/* AVAILABILITY CONTROLS TAB CONTENT */}
        {activeTab === 'settings' && (
          <div className="space-y-6">
            <AvailabilityManager
              availability={availability}
              onUpdateAvailability={async (updatedData) => {
                setAvailability(updatedData);
                try {
                  const docRef = doc(db, 'availability', 'settings');
                  await runTransaction(db, async (transaction) => {
                    const sfDoc = await transaction.get(docRef);
                    const updatePayload = {
                      ...updatedData,
                      expectedReviewDate: updatedData.expectedReviewDate ? Timestamp.fromDate(new Date(updatedData.expectedReviewDate)) : null,
                      expirationDate: updatedData.expirationDate ? Timestamp.fromDate(new Date(updatedData.expirationDate)) : null,
                      updatedAt: serverTimestamp()
                    };
                    if (!sfDoc.exists()) {
                      transaction.set(docRef, updatePayload);
                    } else {
                      transaction.update(docRef, updatePayload);
                    }
                  });
                  setToast({ message: 'Availability settings updated successfully.', type: 'success' });
                } catch (err) {
                  console.error('Failed to update availability settings:', err);
                  setToast({ message: 'Failed to update settings.', type: 'error' });
                }
              }}
            />

            {/* Integration Health Checks Panel */}
            <div className="chrome-panel border border-white/5 bg-charcoal-card/60 p-6 rounded-lg text-slate-200">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="text-sm font-bold text-white uppercase tracking-tight font-display">System Health Integration Checks</h3>
                  <p className="text-3xs text-slate-400 uppercase tracking-wider">Ping diagnostic check to verify API connectivity and storage access</p>
                </div>
                <button
                  type="button"
                  onClick={runHealthChecks}
                  disabled={runningDiagnostics}
                  className="cyber-btn-orange py-1 px-3 text-2xs uppercase font-bold disabled:opacity-50"
                >
                  {runningDiagnostics ? 'Checking...' : 'Run Diagnostics'}
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-charcoal-dark/50 border border-white/5 p-3 rounded-lg flex flex-col justify-between">
                  <span className="text-3xs text-slate-400 uppercase font-bold">Network State</span>
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`w-2.5 h-2.5 rounded-full ${healthStatus.network === 'online' ? 'bg-green-500' : 'bg-red-500'}`} />
                    <span className="text-xs font-bold uppercase text-white">{healthStatus.network}</span>
                  </div>
                </div>

                <div className="bg-charcoal-dark/50 border border-white/5 p-3 rounded-lg flex flex-col justify-between">
                  <span className="text-3xs text-slate-400 uppercase font-bold">Firestore DB</span>
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`w-2.5 h-2.5 rounded-full ${
                      healthStatus.firestore === 'connected' ? 'bg-green-500' :
                      healthStatus.firestore === 'disconnected' ? 'bg-red-500' : 'bg-yellow-500 animate-pulse'
                    }`} />
                    <span className="text-xs font-bold uppercase text-white">{healthStatus.firestore}</span>
                  </div>
                </div>

                <div className="bg-charcoal-dark/50 border border-white/5 p-3 rounded-lg flex flex-col justify-between">
                  <span className="text-3xs text-slate-400 uppercase font-bold">LocalStorage</span>
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`w-2.5 h-2.5 rounded-full ${healthStatus.localStorage === 'functional' ? 'bg-green-500' : 'bg-red-500'}`} />
                    <span className="text-xs font-bold uppercase text-white">{healthStatus.localStorage}</span>
                  </div>
                </div>

                <div className="bg-charcoal-dark/50 border border-white/5 p-3 rounded-lg flex flex-col justify-between">
                  <span className="text-3xs text-slate-400 uppercase font-bold">IndexedDB Engine</span>
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`w-2.5 h-2.5 rounded-full ${healthStatus.indexedDb === 'functional' ? 'bg-green-500' : 'bg-red-500'}`} />
                    <span className="text-xs font-bold uppercase text-white">{healthStatus.indexedDb}</span>
                  </div>
                </div>

                <div className="bg-charcoal-dark/50 border border-white/5 p-3 rounded-lg flex flex-col justify-between">
                  <span className="text-3xs text-slate-400 uppercase font-bold">GPS Permission</span>
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`w-2.5 h-2.5 rounded-full ${
                      healthStatus.geolocation === 'granted' ? 'bg-green-500' :
                      healthStatus.geolocation === 'denied' ? 'bg-red-500' : 'bg-yellow-500'
                    }`} />
                    <span className="text-xs font-bold uppercase text-white">{healthStatus.geolocation}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Diagnostic Log Console */}
            <div className="chrome-panel border border-white/5 bg-charcoal-card/60 p-6 rounded-lg text-slate-200">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="text-sm font-bold text-white uppercase tracking-tight font-display">System Diagnostics Console</h3>
                  <p className="text-3xs text-slate-400 uppercase tracking-wider">Tracks local exceptions, offline warnings, and database errors</p>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      const logStr = logs.map(l => `[${l.timestamp}] [${l.type.toUpperCase()}] ${l.message}`).join('\n');
                      navigator.clipboard.writeText(logStr);
                      setToast({ message: 'Diagnostic logs copied to clipboard!', type: 'success' });
                    }}
                    disabled={logs.length === 0}
                    className="bg-white/5 hover:bg-white/10 border border-white/5 text-white font-bold py-1.5 px-3 rounded text-2xs uppercase disabled:opacity-50"
                  >
                    Copy Logs
                  </button>
                  <button
                    type="button"
                    onClick={() => loggerInstance.clear()}
                    disabled={logs.length === 0}
                    className="bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-400 font-bold py-1.5 px-3 rounded text-2xs uppercase disabled:opacity-50"
                  >
                    Clear Console
                  </button>
                </div>
              </div>

              <div className="bg-charcoal-dark border border-white/10 rounded-lg p-4 font-mono text-2xs leading-relaxed max-h-64 overflow-y-auto space-y-2">
                {logs.map((log, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <span className="text-slate-500 select-none">[{log.timestamp.split('T')[1] ? log.timestamp.split('T')[1].substring(0, 8) : log.timestamp}]</span>
                    <span className={`font-bold ${log.type === 'error' ? 'text-red-400' : 'text-yellow-400'}`}>
                      [{log.type.toUpperCase()}]
                    </span>
                    <span className="text-slate-300 break-all">{log.message}</span>
                  </div>
                ))}
                {logs.length === 0 && (
                  <p className="text-center text-slate-500 py-8 italic font-sans">No diagnostic entries recorded in this session.</p>
                )}
              </div>
            </div>
          </div>
        )}

      </main>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
